import { Link } from "wouter";
import { Lock, FileText, Trophy, Star, AlertCircle, MessageSquare, Trash2, ChevronDown, ListFilter } from "lucide-react";
import { peso } from "../../data/mock";
import { api } from "../../lib/api";
import { messagesHref } from "../../lib/actions";
import { TABS, ATTACHMENT_PREVIEW_COUNT, fmtSize } from "./model";
import { BidderAvatar } from "./BidderAvatar";
import { AttachmentUploader } from "./AttachmentUploader";
import { ConversationForm, AwardedConversationNotice } from "./ProjectConversation";
import { type ProjectDetailView } from "./viewTypes";

export function ProjectDetailTabs({ view }: { view: ProjectDetailView }) {
  const { t, navigate, user, id, tab, setTab, bidData, projectContract, busy, err, setAwardTarget, bidSort, setBidSort, bidSortMenu, setShowAllFiles, setTermsTarget, setDocumentsTarget, attachments, questions, p, isProjectOwner, loadExtras, isOwner, myBid, awarded, sortedBids, winningBid, shortlist, removeAttachment } = view;
  return <div className="card mt-4">
            <div className="px-4 border-b border-line flex gap-1 overflow-x-auto no-scrollbar">
              {TABS.filter((t) => t !== "Bidding" || isOwner).map((t) => (
                <button key={t} onClick={() => setTab(t)} className={`px-4 py-3 text-sm font-semibold whitespace-nowrap border-b-2 -mb-px ${tab === t ? "border-brand-red text-navy" : "border-transparent text-mut hover:text-navy"}`}>
                  {t}{t === "Bidding" ? ` (${isOwner ? bidData?.bids?.length ?? 0 : p.bids})` : ""}{t === "Attachments" ? ` (${attachments.length})` : ""}{t === "Q&A" ? ` (${questions.length})` : ""}
                </button>
              ))}
            </div>
            <div className="p-6">
              {tab === "Project Details" && (
                <div className="space-y-5">
                  <div>
                    <h3 className="font-bold text-navy mb-2">{t("project.descriptionHeading")}</h3>
                    <p className="text-sm text-mut mb-3 whitespace-pre-wrap">{p.description}</p>
                    {p.targetCompletionDate && <p className="text-sm text-navy mb-3"><span className="font-semibold">Client target completion date:</span> {p.targetCompletionDate}</p>}
                    {isProjectOwner && p.budget > 0 && <p className="text-sm text-navy"><span className="font-semibold">{t("cards.privateEstimate", "Your estimate (private)")}:</span> {peso(p.budget)}</p>}
                  </div>
                </div>
              )}
              {tab === "Attachments" && (
                <div className="space-y-3">
                  {isProjectOwner && <AttachmentUploader projectId={id} onUploaded={loadExtras} compact={attachments.length > 0} />}
                  <div className="flex items-center justify-between gap-3">
                    <p className="text-xs text-mut">Select a file to download it.</p>
                    {attachments.length > ATTACHMENT_PREVIEW_COUNT && <button type="button" className="text-xs font-semibold text-navy hover:text-brand-red" onClick={() => setShowAllFiles(true)}>View all →</button>}
                  </div>
                  {attachments.length === 0 ? (
                    <p className="text-sm text-mut">No attachments yet.{isProjectOwner ? " Upload plans, permits, or drawings above." : ""}</p>
                  ) : (
                    <ul className="grid sm:grid-cols-2 xl:grid-cols-3 gap-2">
                      {attachments.slice(0, ATTACHMENT_PREVIEW_COUNT).map((a) => (
                        <li key={a.id} className="relative rounded-lg border border-line p-3 min-w-0">
                          <a href={api.attachmentDownloadUrl(id, a.id)} download={a.filename} className="block pr-7 text-navy hover:text-brand-red">
                            <FileText className="w-5 h-5 mb-2" /><span className="block text-xs font-semibold truncate">{a.filename}</span><span className="block text-[11px] text-mut">{fmtSize(a.size)}</span>
                          </a>
                          {isProjectOwner && <button type="button" onClick={() => removeAttachment(a.id)} className="absolute right-2 top-2 text-brand-red" aria-label={`Delete ${a.filename}`}><Trash2 className="w-4 h-4" /></button>}
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              )}
              {tab === "Bidding" && !isOwner && (
                <div className="text-center py-6">
                  <Lock className="w-8 h-8 text-navy mx-auto mb-2" />
                  <h3 className="font-bold text-navy">Sealed Bidding</h3>
                  <p className="text-sm text-mut max-w-md mx-auto mt-1">
                    Bids are sealed — competitors cannot see each other&apos;s prices. {p.bids} bid{p.bids === 1 ? "" : "s"} received so far.
                    {awarded ? " This project has been awarded." : " The owner shortlists after the deadline."}
                  </p>
                </div>
              )}
          {/* Owner bid management */}
          {isOwner && tab === "Bidding" && (
            <div className="mt-1">
              {err && <div className="mb-3 text-sm text-brand-red flex items-center gap-1"><AlertCircle className="w-4 h-4" />{err}</div>}
              {awarded && <div role="status" className="mb-4 rounded-xl border border-brand-gold/40 bg-brand-gold/5 p-4">
                <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:gap-4">
                  <div className="flex min-w-0 items-start gap-3">
                    <Trophy className="mt-0.5 h-5 w-5 shrink-0 text-brand-gold" />
                    <div className="min-w-0">
                      <div className="font-bold text-navy">{winningBid ? `Awarded to ${winningBid.bidderName}` : "Project awarded"}</div>
                      <p className="mt-1 text-sm text-mut">
                        {projectContract?.offerStatus === "pending" ? "The provider has been sent a contract offer. Both signatures are needed before escrow funding."
                          : projectContract?.offerStatus === "accepted" ? "The provider accepted the contract offer. Review and sign in Contracts before funding escrow."
                            : projectContract ? "Review the contract offer status in Contracts." : "The award is recorded. Contract preparation is pending; check Contracts for the offer once it is ready."}
                      </p>
                    </div>
                  </div>
                  {projectContract && <Link href={`/contracts?contract=${encodeURIComponent(projectContract.id)}`} className="btn-navy h-10 shrink-0 px-4 text-sm sm:ml-auto">View contract</Link>}
                </div>
              </div>}
              {(bidData?.bids?.length ?? 0) === 0 ? (
                <p className="text-sm text-mut">No bids have been submitted yet.</p>
              ) : (
                <div>
                  <div className="mb-4 flex justify-end">
                    <details ref={bidSortMenu} className="relative" onKeyDown={(event) => { if (event.key === "Escape") bidSortMenu.current?.removeAttribute("open"); }}>
                      <summary className="btn-outline flex h-9 cursor-pointer items-center gap-2 px-3 text-xs list-none [&::-webkit-details-marker]:hidden" aria-label="Sort bids">
                        <ListFilter className="h-4 w-4" aria-hidden="true" />
                        <span>{bidSort === "shortest" ? "Shortest duration" : "Lowest price"}</span>
                        <ChevronDown className="h-3.5 w-3.5 text-mut" aria-hidden="true" />
                      </summary>
                      <div className="absolute right-0 z-20 mt-1 w-48 rounded-lg border border-line bg-white p-1 shadow-lift" role="group" aria-label="Sort options">
                        {([ ["lowest", "Lowest price"], ["shortest", "Shortest duration"] ] as const).map(([value, label]) => (
                          <button key={value} type="button" aria-label={label} className={`block w-full rounded-md px-3 py-2 text-left text-sm hover:bg-navy-50 ${bidSort === value ? "bg-navy-50 font-semibold text-navy" : "text-mut"}`}
                            onClick={() => { setBidSort(value); bidSortMenu.current?.removeAttribute("open"); }}>{label}</button>
                        ))}
                      </div>
                    </details>
                  </div>
                  <div className="space-y-3">
                    {sortedBids.map((b: any) => (
                      <article key={b.id} className={`rounded-xl border p-4 ${b.status === "awarded" ? "border-brand-gold/50 bg-brand-gold/5" : "border-line bg-white"}`}>
                        <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
                          <Link href={b.bidderProfileCompanyId ? `/c/${encodeURIComponent(b.bidderProfileCompanyId)}` : `/u/${b.bidderUserId}`} className="group flex min-w-0 items-center gap-3 rounded-lg focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-blue" aria-label={`View ${b.bidderName}'s profile`}>
                            <BidderAvatar name={b.bidderName} src={b.bidderProfileCompanyId ? null : api.apiAssetUrl(b.bidderProfileImageUrl)} />
                            <span className="min-w-0">
                              <span className="block font-bold text-brand-blue underline underline-offset-2 group-hover:text-brand-red">{b.bidderName} <span aria-hidden="true">→</span></span>
                              {b.company && <span className="block text-xs text-mut">Company bid</span>}
                              <span className="block text-xs text-mut">View bidder profile</span>
                            </span>
                          </Link>
                          <div className="flex shrink-0 gap-6 sm:text-right">
                            <div>
                              <div className="text-xs text-mut">Bid price</div>
                              <div className="font-bold text-navy">{peso(b.status === "awarded" && b.finalPrice != null ? b.finalPrice : (b.standingPrice ?? b.price))}</div>
                              {b.inNegotiation && b.standingPrice !== b.price && <div className="text-[11px] text-mut">was {peso(b.price)}</div>}
                            </div>
                            <div>
                              <div className="text-xs text-mut">Duration</div>
                              <div className="font-semibold text-navy">{b.durationText || "—"}</div>
                            </div>
                          </div>
                        </div>
                        <div className="mt-4 flex flex-col gap-3 border-t border-line pt-3 sm:flex-row sm:items-center sm:justify-between">
                          <div className="flex flex-wrap items-center gap-3">
                            {b.terms?.trim() && (
                              <button type="button"
                                className="mt-1 text-[11px] font-semibold text-navy underline underline-offset-2 hover:text-brand-red whitespace-nowrap"
                                onClick={(event) => setTermsTarget({ bidderName: b.bidderName, terms: b.terms, trigger: event.currentTarget })}>
                                View terms &amp; exclusions
                              </button>
                            )}
                            <button type="button" disabled={!b.documents?.length} className="btn-outline h-9 px-3 text-xs disabled:cursor-not-allowed disabled:opacity-50"
                              onClick={() => setDocumentsTarget({ bidId: b.id, bidderName: b.bidderName, documents: b.documents })}>
                              <FileText className="w-3.5 h-3.5" />Documents ({b.documents?.length ?? 0})
                            </button>
                          </div>
                          {awarded ? <span className={`chip ${b.status === "awarded" ? "bg-brand-gold/15 text-[#8a5a00]" : "bg-navy-50 text-mut"}`}>{b.status === "awarded" ? t("activity.status.awarded") : "Not selected"}</span> : <div className="grid grid-cols-2 gap-2 sm:flex sm:flex-wrap sm:justify-end">
                            <button disabled={busy} onClick={() => shortlist(b.id)} className="btn-outline h-9 px-3 text-xs">
                              <Star className="w-3.5 h-3.5" />{b.status === "shortlisted" ? "Unshortlist" : "Shortlist"}
                            </button>
                            <button type="button" disabled={!["submitted", "shortlisted", "in_negotiation", "awarded"].includes(b.status)} onClick={() => {
                              const href = messagesHref(b.bidderUserId, { type: "project", id, name: b.bidderName, subject: p.title, intent: "bid_negotiation", bidId: b.id });
                              if (href) navigate(href);
                            }} className="btn-outline h-9 px-3 text-xs">
                              <MessageSquare className="w-3.5 h-3.5" />Negotiate
                            </button>
                            <button disabled={busy || !!projectContract?.negotiationId} onClick={() => setAwardTarget(b)} className="btn-green col-span-2 h-9 px-3 text-xs">
                              <Trophy className="w-3.5 h-3.5" />{t("projectAgreement.prepare")}
                            </button>
                          </div>}
                        </div>
                      </article>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
              {tab === "Q&A" && (
                <div className="space-y-3">
                  <div>
                    <h3 className="font-bold text-navy flex items-center gap-2"><MessageSquare className="w-4 h-4" />Project conversation</h3>
                    <p className="text-xs text-mut mt-1">Ask for clarification and continue the discussion here before bidding closes.</p>
                  </div>
                  {questions.length === 0 ? (
                    <div className="rounded-lg border border-dashed border-line px-4 py-6 text-center text-sm text-mut">No messages yet.{user ? " Start the conversation." : ""}</div>
                  ) : (
                    <div className="space-y-3">
                      {questions.map((message) => {
                        const isOwnerMessage = message.senderUserId === p.ownerUserId;
                        const isMyMessage = message.senderUserId === user?.id;
                        return (
                          <div key={message.id} className={`flex gap-2 ${isOwnerMessage ? "justify-end" : "justify-start"}`}>
                            <div className={`max-w-[85%] rounded-xl px-3 py-2 text-sm ${isOwnerMessage ? "bg-navy text-white" : "bg-navy-50 text-navy"}`}>
                              <div className={`text-[11px] font-semibold ${isOwnerMessage ? "text-white/75" : "text-mut"}`}>{isMyMessage ? "You" : message.senderName}</div>
                              <p className="mt-0.5 whitespace-pre-wrap">{message.message}</p>
                              <div className={`mt-1 text-[10px] ${isOwnerMessage ? "text-white/65" : "text-mut"}`}>{new Date(message.createdAt).toLocaleString("en-PH", { dateStyle: "medium", timeStyle: "short" })}</div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}
                  {awarded ? (
                    <AwardedConversationNotice isOwner={isProjectOwner} bidStatus={myBid?.status} />
                  ) : user ? (
                    <ConversationForm projectId={id} isOwner={isProjectOwner} onSent={loadExtras} />
                  ) : (
                    <div className="text-sm text-mut border-t border-line pt-3 mt-3">
                      <button onClick={() => navigate("/login")} className="text-brand-red font-semibold">Sign in</button> to join the conversation.
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>;
}
